import type {
  Worker, Admin, DailyCheckin, CheckinInput, Alert, AlertFilter,
  AdminReply, Notice, NoticeInput, NoticeRead, AdminNote, AdminNoteInput,
  DashboardData, CheckinRevision, MonthlyReportData,
  WorkerSignupInput, WorkerSignupRequest, ChangeAdminPasswordInput, RepairWorkerFieldsResult,
  PrivacyConsent, ResolveAttentionInput, MarkAttentionMonitoringInput,
  AttendanceRecord, AttendanceUpdateInput, AttendanceOverviewData, WorkerHomeData,
  AdminMessagesData, WorkerDetailData
} from './types';

// 데이터 서비스 인터페이스 - 향후 DB 교체 시 이 인터페이스만 구현하면 됨
export interface DataService {
  // 인증
  workerLogin(name: string, pin: string): Promise<Worker>;
  adminLogin(loginId: string, password: string): Promise<Admin | null>;

  // 취업자 관리
  getWorkers(): Promise<Worker[]>;
  getWorker(workerId: string): Promise<Worker | null>;
  createWorker(data: Omit<Worker, 'worker_id' | 'created_at' | 'updated_at'>): Promise<Worker>;
  updateWorker(workerId: string, data: Partial<Worker>): Promise<Worker>;
  deleteWorker(workerId: string, terminationDate?: string, reason?: string): Promise<void>;
  terminateWorker(workerId: string, terminationDate: string, reason: string): Promise<void>;

  // 취업자 자기등록
  createWorkerSignupRequest(data: WorkerSignupInput): Promise<WorkerSignupRequest>;
  getWorkerSignupRequests(status?: 'pending' | 'approved' | 'rejected'): Promise<WorkerSignupRequest[]>;
  approveWorkerSignupRequest(requestId: string, adminId: string, adminMemo?: string): Promise<Worker>;
  rejectWorkerSignupRequest(requestId: string, adminId: string, adminMemo?: string): Promise<WorkerSignupRequest>;
  checkWorkerSignupStatus(name: string, pin: string): Promise<WorkerSignupRequest | null>;

  // 관리자 관리
  getAdmins(): Promise<Admin[]>;
  createAdmin(data: Omit<Admin, 'admin_id' | 'created_at' | 'updated_at'>): Promise<Admin>;
  updateAdmin(adminId: string, data: Partial<Admin>): Promise<Admin>;
  changeAdminPassword(data: ChangeAdminPasswordInput): Promise<void>;

  // 체크인
  getCheckins(workerId: string, month?: string): Promise<DailyCheckin[]>;
  getCheckinByDate(workerId: string, date: string): Promise<DailyCheckin | null>;
  getTodayCheckin(workerId: string): Promise<DailyCheckin | null>;
  createCheckin(data: CheckinInput): Promise<DailyCheckin>;
  updateCheckin(checkinId: string, data: CheckinInput): Promise<DailyCheckin>;
  getCheckinRevisions(checkinId: string): Promise<CheckinRevision[]>;

  // 알림
  getAlerts(filters?: AlertFilter): Promise<Alert[]>;
  updateAlertStatus(alertId: string, status: string, memo?: string): Promise<void>;
  resolveAttentionAlerts(data: ResolveAttentionInput): Promise<void>;
  markAttentionMonitoring(data: MarkAttentionMonitoringInput): Promise<void>;

  // 답변
  getReplies(workerId: string): Promise<AdminReply[]>;
  getUnrepliedMessages(): Promise<AdminReply[]>;
  createReply(checkinId: string, adminId: string, replyText: string): Promise<AdminReply>;
  markReplyRead(replyId: string): Promise<void>;

  // 공지
  getNotices(activeOnly?: boolean): Promise<Notice[]>;
  createNotice(data: NoticeInput, adminId: string): Promise<Notice>;
  updateNotice(noticeId: string, data: Partial<NoticeInput>): Promise<Notice>;
  getNoticeReads(noticeId: string): Promise<NoticeRead[]>;
  recordNoticeRead(noticeId: string, userId: string, userType: string): Promise<void>;

  // 상담 기록
  getAdminNotes(workerId: string): Promise<AdminNote[]>;
  createAdminNote(data: AdminNoteInput, adminId: string): Promise<AdminNote>;

  // 대시보드
  getDashboardData(): Promise<DashboardData>;

  // 월별 통계 보고서
  getMonthlyReport(year: string, month: string): Promise<MonthlyReportData>;

  // 잘못 들어간 필드 복구 (Apps Script 실행)
  repairWorkerFields(dryRun?: boolean): Promise<RepairWorkerFieldsResult>;

  // 개인정보 동의 및 보관 관리
  getPrivacyConsents(): Promise<PrivacyConsent[]>;

  // 취업자 홈 통합 조회
  getWorkerHome(workerId: string): Promise<WorkerHomeData>;
  getAdminMessages(): Promise<AdminMessagesData>;
  getWorkerDetail(workerId: string): Promise<WorkerDetailData>;

  // 간편 출퇴근
  getTodayAttendance(workerId: string): Promise<AttendanceRecord | null>;
  getAttendance(workerId?: string, month?: string, date?: string): Promise<AttendanceRecord[]>;
  getAttendanceOverview(date: string, search?: string, status?: string): Promise<AttendanceOverviewData>;
  clockIn(workerId: string, workerMemo?: string): Promise<AttendanceRecord>;
  clockOut(workerId: string, workerMemo?: string): Promise<AttendanceRecord>;
  updateAttendance(attendanceId: string, data: AttendanceUpdateInput): Promise<AttendanceRecord>;
}

// ===== API 서비스 팩토리 및 싱글톤 수출 =====
import { GoogleSheetsService } from './googleSheetsService';
import { LocalStorageService } from './localStorageService';

// VITE_API_URL 환경 변수가 유효하게 선언되어 있을 때만 Google Sheets 실시간 API 모드를 활성화합니다.
const googleSheetsApiUrl = import.meta.env.VITE_API_URL;

export const apiService: DataService = googleSheetsApiUrl
  ? new GoogleSheetsService(googleSheetsApiUrl)
  : new LocalStorageService();
